import { useEffect } from 'react';

import { LAYER_NORM_NONE } from '../../../constants/layers.ts';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import {
  removeLamp,
  toggleLayerIllumination,
  toggleLayerLuminaireHolder,
  updateLayerNorm,
} from '../../../store/reducers/workspace.reducer.ts';
import { selectActiveVersion } from '../../../store/selectors/workspace.selector.ts';
import { canModifyWorkspace } from '../../../utils/jobStatus.ts';

const isTyping = () => {
  const tag = document.activeElement?.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
};

/**
 * The workspace's editing shortcuts (Delete removes the selected luminaire, ⌘/Ctrl+Shift+1/2
 * toggle CHL / LMN on the selected layer). They lived in the legacy sidebar and died with it
 * when it was closed; here they belong to the page, whether the panel is open or not.
 */
export function useWorkspaceShortcuts(enabled: boolean) {
  const dispatch = useAppDispatch();
  const lamps = useAppSelector((s) => s.workspace.lamps);
  const modelLayers = useAppSelector((s) => s.workspace.modelLayers);
  const selectedLayer = useAppSelector((s) => s.workspace.selectedLayer);
  const canModify = canModifyWorkspace(useAppSelector(selectActiveVersion));

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (isTyping() || !canModify) return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        const selected = lamps.find((lamp) => lamp.isSelected);
        if (selected) {
          e.preventDefault();
          dispatch(removeLamp(selected.id));
        }
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.code === 'Digit1' || e.code === 'Digit2')) {
        const layer = selectedLayer ? modelLayers.find((l) => l.threeJsName === selectedLayer) : undefined;
        if (!layer) return;
        e.preventDefault();
        if (e.code === 'Digit1') {
          dispatch(toggleLayerLuminaireHolder(layer.threeJsName));
        } else {
          dispatch(toggleLayerIllumination(layer.threeJsName));
          if (layer.isIlluminated) dispatch(updateLayerNorm({ name: layer.threeJsName, norm: LAYER_NORM_NONE }));
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled, canModify, lamps, modelLayers, selectedLayer, dispatch]);
}
